import { type Dispatch, type SetStateAction } from 'react';
import { useNotify, useTranslate } from 'ra-core';
import { type Accept, useDropzone } from 'react-dropzone';
import { FileText, X } from 'lucide-react';

const MAX_AMOUNT_OF_FILES = 10;

export const LlmInsightMimeTypes: Accept = {
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'text/xml': ['.xml'],
  'application/json': ['.json'],
  'application/pdf': ['.pdf'],
  'image/bmp': ['.bmp'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

export const LlmFilesDropzone = ({
  disabled,
  files,
  onFilesChange,
}: {
  disabled?: boolean;
  files: File[];
  onFilesChange: Dispatch<SetStateAction<File[]>>;
}) => {
  const notify = useNotify();
  const translate = useTranslate();

  const removeFile = (fileToRemove: File) => {
    onFilesChange((currentFiles) =>
      currentFiles.filter((file) => file !== fileToRemove)
    );
  };

  const dropzone = useDropzone({
    multiple: true,
    maxFiles: MAX_AMOUNT_OF_FILES,
    accept: LlmInsightMimeTypes,
    onDrop(acceptedFiles) {
      if (acceptedFiles.length === 0) {
        return;
      }

      onFilesChange((currentFiles) => {
        const availableSlots = Math.max(
          MAX_AMOUNT_OF_FILES - currentFiles.length,
          0
        );
        const nextFiles = acceptedFiles
          .slice(0, availableSlots)
          .map((file) => file);

        if (acceptedFiles.length > availableSlots) {
          notify(
            translate('moonstar.unitPerformance.files.tooMany', {
              max: MAX_AMOUNT_OF_FILES,
            }),
            { type: 'warning' }
          );
        }

        return [...currentFiles, ...nextFiles];
      });
    },
    onDropRejected() {
      notify(translate('moonstar.unitPerformance.files.rejected'), {
        type: 'warning',
      });
    },
    disabled: disabled || files.length >= MAX_AMOUNT_OF_FILES,
  });

  return (
    <div
      {...dropzone.getRootProps()}
      className={`flex min-h-[120px] flex-1 items-center justify-center rounded-md border border-dashed px-4 py-3 ${
        files.length > 0
          ? 'border-primary bg-muted/50'
          : 'border-border bg-transparent'
      } ${
        disabled || files.length >= MAX_AMOUNT_OF_FILES
          ? 'cursor-default'
          : 'cursor-pointer'
      } mb-2`}
    >
      <input {...dropzone.getInputProps()} />
      {files.length === 0 ? (
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-muted-foreground">
            {translate('moonstar.unitPerformance.files.prompt')}
          </p>
          <p className="text-xs text-muted-foreground">
            {translate('moonstar.unitPerformance.files.hint', {
              max: MAX_AMOUNT_OF_FILES,
            })}
          </p>
        </div>
      ) : (
        <div className="w-full space-y-2">
          <p className="text-xs text-muted-foreground">
            {translate('moonstar.unitPerformance.files.selected', {
              selected: files.length,
              max: MAX_AMOUNT_OF_FILES,
            })}
          </p>

          {files.map((file) => (
            <div
              key={`${file.name}-${file.lastModified}`}
              className="flex items-center justify-between gap-2 rounded-md bg-background px-2 py-1.5"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <FileText className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm">{file.name}</span>
              </div>

              <button
                type="button"
                className="rounded-md p-1 hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
                disabled={disabled}
                aria-label={translate('moonstar.unitPerformance.files.remove', {
                  name: file.name,
                })}
                onClick={(event) => {
                  event.stopPropagation();
                  removeFile(file);
                }}
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
